import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../i18n';
import { 
  Moon, Waves, ArrowUp, ArrowDown, Clock, MapPin, 
  ChevronDown, Thermometer
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import EmmeGuide, { useEmmeGuide, EmmeHelpButton } from './EmmeGuide';

// Calcolo fase lunare (algoritmo semplificato ma accurato)
const getMoonPhase = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  // Algoritmo di Conway per la fase lunare
  let r = year % 100;
  r %= 19;
  if (r > 9) r -= 19;
  r = ((r * 11) % 30) + month + day;
  if (month < 3) r += 2;
  r -= ((year < 2000) ? 4 : 8.3);
  r = Math.floor(r + 0.5) % 30;
  if (r < 0) r += 30;
  
  // Determina la fase (0-29)
  const phase = r;
  
  // Nomi e icone delle fasi
  if (phase === 0 || phase === 29) return { name: 'Luna Nuova', emoji: '🌑', illumination: 0, phase: 'new' };
  if (phase >= 1 && phase <= 6) return { name: 'Luna Crescente', emoji: '🌒', illumination: Math.round((phase / 7) * 50), phase: 'waxing_crescent' };
  if (phase >= 7 && phase <= 8) return { name: 'Primo Quarto', emoji: '🌓', illumination: 50, phase: 'first_quarter' };
  if (phase >= 9 && phase <= 13) return { name: 'Gibbosa Crescente', emoji: '🌔', illumination: 50 + Math.round(((phase - 8) / 6) * 50), phase: 'waxing_gibbous' };
  if (phase >= 14 && phase <= 16) return { name: 'Luna Piena', emoji: '🌕', illumination: 100, phase: 'full' };
  if (phase >= 17 && phase <= 21) return { name: 'Gibbosa Calante', emoji: '🌖', illumination: 100 - Math.round(((phase - 16) / 6) * 50), phase: 'waning_gibbous' };
  if (phase >= 22 && phase <= 23) return { name: 'Ultimo Quarto', emoji: '🌗', illumination: 50, phase: 'last_quarter' };
  return { name: 'Luna Calante', emoji: '🌘', illumination: Math.round((1 - (phase - 23) / 6) * 50), phase: 'waning_crescent' };
};

// Calcola prossime fasi lunari
const getUpcomingMoonPhases = () => {
  const phases = [];
  const today = new Date();
  
  for (let i = 0; i < 30; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    const phase = getMoonPhase(date);
    
    // Aggiungi solo le fasi principali
    if (['new', 'first_quarter', 'full', 'last_quarter'].includes(phase.phase)) {
      // Evita duplicati
      if (phases.length === 0 || phases[phases.length - 1].phase !== phase.phase) {
        phases.push({
          ...phase,
          date: date,
          dateStr: date.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })
        });
      }
    }
  }
  
  return phases.slice(0, 4);
};

// Stima maree basata sulla luna (semplificata)
const getTideEstimate = (moonPhase) => {
  if (moonPhase.phase === 'new' || moonPhase.phase === 'full') {
    return { type: 'Maree Vive', description: 'Escursione massima', strength: 'high', color: 'text-cyan-400' };
  }
  if (moonPhase.phase === 'first_quarter' || moonPhase.phase === 'last_quarter') {
    return { type: 'Maree Morte', description: 'Escursione minima', strength: 'low', color: 'text-slate-400' };
  }
  if (moonPhase.illumination > 75 || moonPhase.illumination < 25) {
    return { type: 'Maree Forti', description: 'Escursione significativa', strength: 'medium-high', color: 'text-blue-400' };
  }
  return { type: 'Maree Medie', description: 'Escursione moderata', strength: 'medium', color: 'text-blue-300' };
};

// Porti mediterranei
const TIDE_PORTS = [
  { name: 'Venezia', country: '🇮🇹', lat: 45.4408, lon: 12.3155 },
  { name: 'Trieste', country: '🇮🇹', lat: 45.6495, lon: 13.7768 },
  { name: 'Genova', country: '🇮🇹', lat: 44.4056, lon: 8.9463 },
  { name: 'Napoli', country: '🇮🇹', lat: 40.8518, lon: 14.2681 },
  { name: 'Marsiglia', country: '🇫🇷', lat: 43.2965, lon: 5.3698 },
  { name: 'Barcellona', country: '🇪🇸', lat: 41.3851, lon: 2.1734 },
  { name: 'Atene', country: '🇬🇷', lat: 37.9838, lon: 23.7275 },
  { name: 'Dubrovnik', country: '🇭🇷', lat: 42.6507, lon: 18.0944 },
];

// Punti di campionamento temperatura
const SAMPLE_POINTS = [
  { lat: 43.5, lon: 7.5, name: 'Costa Azzurra' },
  { lat: 41.9, lon: 12.5, name: 'Tirreno Centrale' },
  { lat: 38.5, lon: 15.5, name: 'Stretto Messina' },
  { lat: 36.8, lon: 14.5, name: 'Canale Sicilia' },
  { lat: 45.3, lon: 12.3, name: 'Alto Adriatico' },
  { lat: 42.5, lon: 14.5, name: 'Adriatico Centrale' },
  { lat: 40.5, lon: 18.5, name: 'Basso Adriatico' },
  { lat: 35.5, lon: 24.0, name: 'Creta' },
  { lat: 39.5, lon: 3.0, name: 'Baleari' },
  { lat: 37.0, lon: -1.0, name: 'Costa Spagna' },
  { lat: 37.5, lon: 25.5, name: 'Mar Egeo' },
  { lat: 35.0, lon: 33.0, name: 'Cipro' },
  { lat: 37.0, lon: 10.5, name: 'Golfo Tunisi' },
];

// Componente Mappa Termica Temperature Acqua
const SeaTempMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [tempData, setTempData] = useState([]);

  useEffect(() => {
    const fetchTemperatures = async () => {
      setLoading(true);
      
      const promises = SAMPLE_POINTS.map(async (point) => {
        try {
          const url = `https://marine-api.open-meteo.com/v1/marine?latitude=${point.lat}&longitude=${point.lon}&hourly=sea_surface_temperature&timezone=Europe%2FRome&forecast_days=1`;
          const res = await fetch(url);
          if (res.ok) {
            const data = await res.json();
            const temps = data?.hourly?.sea_surface_temperature;
            if (temps && temps.length > 0) {
              const validTemps = temps.filter(t => t !== null);
              if (validTemps.length > 0) {
                const avgTemp = validTemps.reduce((a, b) => a + b, 0) / validTemps.length;
                return { ...point, temp: avgTemp };
              }
            }
          }
        } catch (e) {
          console.log('Error fetching temp for', point.name);
        }
        return null;
      });

      const allResults = await Promise.all(promises);
      setTempData(allResults.filter(r => r !== null));
      setLoading(false);
    };

    fetchTemperatures();
  }, []);

  useEffect(() => {
    if (!mapRef.current || loading) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: true
      }).setView([40, 15], 5);
      
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap, © CARTO',
        maxZoom: 10
      }).addTo(mapInstanceRef.current);
    }

    const map = mapInstanceRef.current;

    // Colore basato sulla temperatura
    const getTempColor = (temp) => {
      if (temp < 14) return '#3B82F6';
      if (temp < 17) return '#06B6D4';
      if (temp < 20) return '#10B981';
      if (temp < 23) return '#84CC16';
      if (temp < 26) return '#EAB308';
      if (temp < 28) return '#F97316';
      return '#EF4444';
    };

    // Aggiungi cerchi per ogni punto
    tempData.forEach(point => {
      const color = getTempColor(point.temp);
      
      L.circle([point.lat, point.lon], {
        radius: 80000,
        fillColor: color,
        fillOpacity: 0.3,
        stroke: false
      }).addTo(map);

      const icon = L.divIcon({
        className: 'temp-marker',
        html: `<div style="background:${color};color:white;font-weight:bold;font-size:12px;padding:4px 8px;border-radius:12px;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);white-space:nowrap;">${point.temp.toFixed(1)}°</div>`,
        iconSize: [50, 24],
        iconAnchor: [25, 12]
      });

      L.marker([point.lat, point.lon], { icon })
        .addTo(map)
        .bindPopup(`<div style="text-align:center;"><strong>${point.name}</strong><br/><span style="font-size:18px;color:${color};font-weight:bold;">${point.temp.toFixed(1)}°C</span></div>`);
    });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.eachLayer(layer => {
          if (layer instanceof L.Circle || layer instanceof L.Marker) {
            layer.remove();
          }
        });
      }
    };
  }, [tempData, loading]);

  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 bg-slate-900/80 z-10 flex items-center justify-center rounded-xl">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Caricamento temperature...</p>
          </div>
        </div>
      )}
      <div ref={mapRef} style={{ width: '100%', height: '300px', borderRadius: '12px' }} />
      
      <div className="flex items-center justify-center gap-1 mt-3 flex-wrap">
        <span className="text-xs text-slate-500">Freddo</span>
        {['#3B82F6', '#06B6D4', '#10B981', '#84CC16', '#EAB308', '#F97316', '#EF4444'].map((color, i) => (
          <div key={i} style={{ background: color, width: '24px', height: '12px', borderRadius: '2px' }} />
        ))}
        <span className="text-xs text-slate-500">Caldo</span>
      </div>
    </div>
  );
};

// Componente principale
const MareLunaSection = () => {
  const [selectedPort, setSelectedPort] = useState(TIDE_PORTS[0]);
  const [showPortList, setShowPortList] = useState(false);
  const [activeTab, setActiveTab] = useState('luna');
  
  // Guida Emme
  const { showGuide, closeGuide, openGuide } = useEmmeGuide('mareluna');

  const currentMoonPhase = getMoonPhase();
  const upcomingPhases = getUpcomingMoonPhases();
  const tideEstimate = getTideEstimate(currentMoonPhase);

  // Orari approssimativi maree
  const moonriseHour = (6 + Math.floor(currentMoonPhase.illumination / 10)) % 24;
  const highTide1 = `${String(moonriseHour).padStart(2, '0')}:15`;
  const lowTide1 = `${String((moonriseHour + 6) % 24).padStart(2, '0')}:30`;
  const highTide2 = `${String((moonriseHour + 12) % 24).padStart(2, '0')}:45`;
  const lowTide2 = `${String((moonriseHour + 18) % 24).padStart(2, '0')}:00`;

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Guida Emme */}
      {showGuide && <EmmeGuide sectionId="mareluna" onClose={closeGuide} />}
      <EmmeHelpButton onClick={openGuide} />
      
      {/* Header */}
      <div className="mb-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center">
          <Moon className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Barlow Condensed'}}>
          MARE & LUNA
        </h2>
        <p className="text-purple-400 text-sm">Fasi lunari, maree e temperature</p>
      </div>

      {/* Tabs */}
      <div className="flex bg-slate-900/60 rounded-xl p-1 mb-4">
        <button
          onClick={() => setActiveTab('luna')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
            activeTab === 'luna' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
          }`}
        >
          🌙 Luna
        </button>
        <button
          onClick={() => setActiveTab('maree')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
            activeTab === 'maree' ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'
          }`}
        >
          🌊 Maree
        </button>
        <button
          onClick={() => setActiveTab('mappa')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
            activeTab === 'mappa' ? 'bg-orange-600 text-white' : 'text-slate-400 hover:text-white'
          }`}
        >
          🌡️ Temp.
        </button>
      </div>

      {/* Tab: Luna */}
      {activeTab === 'luna' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-slate-900 to-indigo-950 border border-purple-500/30 rounded-2xl p-6 text-center">
            <p className="text-slate-400 text-sm mb-2">Fase Lunare Attuale</p>
            <div className="text-7xl mb-3">{currentMoonPhase.emoji}</div>
            <h3 className="text-2xl font-bold text-white mb-2">{currentMoonPhase.name}</h3>
            <div className="flex items-center justify-center gap-2">
              <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-yellow-400 rounded-full transition-all"
                  style={{ width: `${currentMoonPhase.illumination}%` }}
                />
              </div>
              <span className="text-purple-400 font-medium">{currentMoonPhase.illumination}%</span>
            </div>
            <p className="text-slate-500 text-sm mt-2">Illuminazione</p>
          </div>

          <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
            <h4 className="text-white font-bold mb-3">Prossime Fasi</h4>
            <div className="grid grid-cols-4 gap-2">
              {upcomingPhases.map((phase, idx) => (
                <div key={idx} className="text-center p-2 bg-slate-800/50 rounded-lg">
                  <div className="text-2xl mb-1">{phase.emoji}</div>
                  <p className="text-xs text-slate-400">{phase.dateStr}</p>
                </div>
              ))}
            </div>
          </div>

          <div className={`bg-slate-900/60 border rounded-xl p-4 ${
            tideEstimate.strength === 'high' ? 'border-cyan-500/50' : 'border-slate-700'
          }`}>
            <div className="flex items-center gap-3">
              <Waves className={`w-8 h-8 ${tideEstimate.color}`} />
              <div>
                <h4 className={`font-bold ${tideEstimate.color}`}>{tideEstimate.type}</h4>
                <p className="text-slate-400 text-sm">{tideEstimate.description}</p>
              </div>
            </div>
            {tideEstimate.strength === 'high' && (
              <p className="text-xs text-cyan-400/70 mt-2">
                Durante luna piena/nuova le maree raggiungono la massima escursione
              </p>
            )}
          </div>
        </div>
      )}

      {/* Tab: Maree */}
      {activeTab === 'maree' && (
        <div className="space-y-4">
          <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowPortList(!showPortList)}
                className="flex items-center gap-2 text-white"
              >
                <MapPin className="w-5 h-5 text-cyan-400" />
                <span>{selectedPort.country} {selectedPort.name}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showPortList ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            {showPortList && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {TIDE_PORTS.map(port => (
                  <button
                    key={port.name}
                    onClick={() => { setSelectedPort(port); setShowPortList(false); }}
                    className={`px-2 py-1.5 rounded text-sm text-left flex items-center gap-1 ${
                      selectedPort.name === port.name
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    <span>{port.country}</span>
                    <span className="truncate">{port.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-slate-900 to-cyan-950 border border-cyan-500/30 rounded-2xl p-4">
            <h4 className="text-white font-bold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              Maree Oggi (stima)
            </h4>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-cyan-500/10 rounded-xl p-3 text-center">
                <ArrowUp className="w-6 h-6 text-cyan-400 mx-auto mb-1" />
                <p className="text-cyan-400 font-bold text-xl">{highTide1}</p>
                <p className="text-slate-400 text-xs">Alta marea</p>
              </div>
              <div className="bg-slate-700/30 rounded-xl p-3 text-center">
                <ArrowDown className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                <p className="text-slate-300 font-bold text-xl">{lowTide1}</p>
                <p className="text-slate-400 text-xs">Bassa marea</p>
              </div>
              <div className="bg-cyan-500/10 rounded-xl p-3 text-center">
                <ArrowUp className="w-6 h-6 text-cyan-400 mx-auto mb-1" />
                <p className="text-cyan-400 font-bold text-xl">{highTide2}</p>
                <p className="text-slate-400 text-xs">Alta marea</p>
              </div>
              <div className="bg-slate-700/30 rounded-xl p-3 text-center">
                <ArrowDown className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                <p className="text-slate-300 font-bold text-xl">{lowTide2}</p>
                <p className="text-slate-400 text-xs">Bassa marea</p>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-3 text-center">
              Orari stimati basati sulla fase lunare. Per dati precisi consulta le autorità portuali.
            </p>
          </div>

          <div className="bg-slate-900/60 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <span className="text-4xl">{currentMoonPhase.emoji}</span>
              <div>
                <p className="text-white font-medium">{currentMoonPhase.name}</p>
                <p className={`text-sm ${tideEstimate.color}`}>{tideEstimate.type}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Mappa Temperature */}
      {activeTab === 'mappa' && (
        <div className="space-y-4">
          <div className="bg-slate-900/60 border border-orange-500/30 rounded-2xl p-4">
            <h4 className="text-white font-bold mb-3 flex items-center gap-2">
              <Thermometer className="w-5 h-5 text-orange-400" />
              Temperature Superficie Mare
            </h4>
            <SeaTempMap />
            <p className="text-xs text-slate-500 mt-3 text-center">
              Dati: Open-Meteo Marine API
            </p>
          </div>
        </div>
      )}

      <p className="text-center text-slate-600 text-xs mt-6">
        Calcoli astronomici e dati Open-Meteo
      </p>
    </div>
  );
};

export default MareLunaSection;
